import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getAuthConfig, getGitHubSignInUrl, getGoogleSignInUrl } from "../api/authApi";
import OAuthProviderButton from "../components/auth/OAuthProviderButton";
import { MIDPOINT_LOGO_WHITE_SRC } from "../constants/midpointBrand";
import { DEFAULT_POST_LOGIN_HASH } from "../auth/adminAccess";
import { getDefaultPostLoginDestination } from "../auth/partnerAccess";
import { useAuth } from "../auth/useAuth";
import { applyReturnToNavigation } from "../utils/sanitizeReturnTo";
import "../styles/loginPage.css";

const OAUTH_ERROR_MESSAGES = {
  google_auth_failed:
    "Google sign-in failed. Your account may not have access yet.",
  github_auth_failed:
    "GitHub sign-in failed. Your account may not have access yet.",
  google_auth_disabled: "Google sign-in is not available.",
  github_auth_disabled: "GitHub sign-in is not available.",
};

const PLATFORM_CAPABILITIES = [
  {
    icon: "◈",
    title: "Community intelligence map",
    copy: "Real-time layers for markets, safety, weather, infrastructure, and cultural signals.",
  },
  {
    icon: "◎",
    title: "Client Views platform",
    copy: "Branded map experiences for AutoMapa, CasaMapa, and custom market portals.",
  },
  {
    icon: "⬡",
    title: "Administration & IAM",
    copy: "Operator tools, data pipelines, partner workspaces, and role-based access control.",
  },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading, sessionMessage, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [googleAuthEnabled, setGoogleAuthEnabled] = useState(false);
  const [githubAuthEnabled, setGithubAuthEnabled] = useState(false);

  const intendedReturnTo = useMemo(() => {
    const fromHash = location.state?.fromHash;
    if (typeof fromHash === "string" && fromHash.length > 0) {
      return fromHash;
    }
    const fromPath = location.state?.from;
    if (typeof fromPath === "string" && fromPath.length > 0 && fromPath !== "/login") {
      return fromPath;
    }
    const partnerDefault = getDefaultPostLoginDestination(user);
    if (partnerDefault) {
      return partnerDefault;
    }
    return DEFAULT_POST_LOGIN_HASH;
  }, [location.state?.from, location.state?.fromHash, user]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const config = await getAuthConfig();
        if (!cancelled) {
          setGoogleAuthEnabled(Boolean(config?.googleAuthEnabled));
          setGithubAuthEnabled(Boolean(config?.githubAuthEnabled));
        }
      } catch {
        if (!cancelled) {
          setGoogleAuthEnabled(false);
          setGithubAuthEnabled(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const errorCode = searchParams.get("error");
    if (errorCode && OAUTH_ERROR_MESSAGES[errorCode]) {
      setErrorMessage(OAUTH_ERROR_MESSAGES[errorCode]);
    }
  }, [searchParams]);

  const passwordUpdated = searchParams.get("passwordUpdated") === "1";

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      if (user?.consent?.requiresConsent) {
        navigate("/account/consent-required", {
          replace: true,
          state: { returnTo: intendedReturnTo },
        });
        return;
      }

      applyReturnToNavigation(intendedReturnTo, navigate);
    }
  }, [intendedReturnTo, isAuthenticated, isLoading, navigate, user?.consent?.requiresConsent]);

  const handleGoogleSignIn = () => {
    window.location.href = getGoogleSignInUrl(intendedReturnTo);
  };

  const handleGitHubSignIn = () => {
    window.location.href = getGitHubSignInUrl(intendedReturnTo);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail || !password) {
      setErrorMessage("Email and password are required.");
      return;
    }

    try {
      setSubmitting(true);
      await login(normalizedEmail, password);
      applyReturnToNavigation(intendedReturnTo, navigate);
    } catch (error) {
      setErrorMessage(error.message || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  const oauthBusy = submitting;

  return (
    <div className="sip-login">
      <aside className="sip-login__brand" aria-label="Platform overview">
        <div className="sip-login__brand-inner">
          <Link to="/" className="sip-login__logo-row" aria-label="Midpoint public home">
            <img src={MIDPOINT_LOGO_WHITE_SRC} alt="" className="sip-login__logo" />
            <span className="sip-login__company">Midpoint Media</span>
          </Link>

          <p className="sip-login__kicker">Signal Intelligence Platform</p>
          <h1 className="sip-login__title">See the signals behind every market</h1>
          <p className="sip-login__lead">
            Sign in to access the operator console, client portals, and map-driven intelligence
            workspaces that power Midpoint&apos;s community and market platforms.
          </p>

          <ul className="sip-login__capabilities">
            {PLATFORM_CAPABILITIES.map((item) => (
              <li key={item.title} className="sip-login__capability">
                <span className="sip-login__capability-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.copy}</span>
                </div>
              </li>
            ))}
          </ul>

          <a
            className="sip-login__media-cta"
            href="https://midpointmedia.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open the Midpoint home page at midpointmedia.com"
          >
            <span className="sip-login__media-cta-copy">
              <strong>Open Midpoint home page</strong>
              <span>midpointmedia.com · opens in a new tab</span>
            </span>
            <span className="sip-login__media-cta-arrow" aria-hidden="true">
              ↗
            </span>
          </a>
        </div>

        <p className="sip-login__brand-foot">
          Access is limited to invited operators, partners, and client administrators.
        </p>
      </aside>

      <main className="sip-login__panel">
        <div className="sip-login__card">
          <header className="sip-login__card-head">
            <p className="sip-login__eyebrow">Secure access</p>
            <h1>Sign in</h1>
            <p>
              Authenticate to the Midpoint operator platform — administration, data pipelines,
              Client Views, and IAM.
            </p>
          </header>

          {sessionMessage ? (
            <p className="sip-login__message sip-login__message--status" role="status">
              {sessionMessage}
            </p>
          ) : null}

          {passwordUpdated ? (
            <p className="sip-login__message sip-login__message--status" role="status">
              Password updated. Please sign in.
            </p>
          ) : null}

          <div className="sip-login__oauth">
            <OAuthProviderButton
              provider="google"
              onClick={handleGoogleSignIn}
              disabled={oauthBusy || !googleAuthEnabled}
            />
            <OAuthProviderButton
              provider="github"
              onClick={handleGitHubSignIn}
              disabled={oauthBusy || !githubAuthEnabled}
            />
          </div>

          <p className="sip-login__divider" aria-hidden="true">
            or
          </p>

          <form className="sip-login__form" onSubmit={handleSubmit}>
            <label className="sip-login__field">
              <span>Email</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                placeholder="you@company.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
                required
              />
            </label>

            <label className="sip-login__field">
              <span>Password</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={submitting}
                required
              />
            </label>

            {errorMessage ? (
              <p className="sip-login__message sip-login__message--error" role="alert">
                {errorMessage}
              </p>
            ) : null}

            <button type="submit" className="sip-login__submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in with email"}
            </button>
          </form>

          <p className="sip-login__footnote">
            <Link to="/forgot-password">Forgot your password?</Link>
          </p>

          <p className="sip-login__access-note">
            New accounts are created through invitation or OAuth registration when enabled by your
            organization.
          </p>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
